import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { format } from 'date-fns';

import OrdersApi from '../../services/api/OrdersApi';
import { ORDERS_SEARCH_FILTERS, SORT_DIRECTION } from '../../types';
import { RootState } from '../../types';
import { showError } from '../../utils/utils';

const setFiltersQuery = createAction<string>('orders/setFiltersQuery');
const setOrdersFilter = createAction<{
  type: 'refNumber' | 'amount' | 'period';
  value: SORT_DIRECTION;
}>('orders/setFilter');
const setOrdersLoading = createAction<{
  type: 'orders' | 'roles';
  value: boolean;
}>('orders/setLoading');
const setSelectFilter = createAction<{
  type: 'role' | 'orderStatuses';
  value: string | string[];
}>('orders/setSelectFilter');
const setPeriodData = createAction<{ from: Date | null; to: Date | null }>(
  'orders/setPeriodData'
);
const setPage = createAction<number>('orders/setPaginationPage');
const setInitialOrders = createAction('orders/setInitialState');

const fetchUserOrders = createAsyncThunk(
  'orders/fetchUserOrders',
  async (payload: void | { concat?: boolean }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setOrdersLoading({ type: 'orders', value: true }));
      const rootState = thunkAPI.getState() as RootState;
      const {
        role,
        refNumber,
        search,
        page,
        amount,
        orderStatuses,
        period,
      } = rootState.orders.data.filters;

      const filtersPayload: Partial<ORDERS_SEARCH_FILTERS> & {
        accountId: number;
        period?: { from: string; to: string };
        role?: string;
      } = {
        accountId: rootState.auth.account.id,
      };
      if (amount.show) {
        filtersPayload.amount = amount;
      }
      if (period.show) {
        filtersPayload.period = {
          ...period,
          from: '',
          to: '',
        };
      }

      if (role) {
        filtersPayload.role = role;
      }
      if (
        rootState.orders.data.filters.periodData.from &&
        rootState.orders.data.filters.periodData.to
      ) {
        filtersPayload.period = {
          from: format(
            new Date(rootState.orders.data.filters.periodData.from),
            'yyyy-MM-dd'
          ),
          to: format(
            new Date(rootState.orders.data.filters.periodData.to),
            'yyyy-MM-dd'
          ),
          direction: period.direction,
        };
      }
      filtersPayload.page = page;
      filtersPayload.orderStatuses = orderStatuses ? [orderStatuses] : [];
      filtersPayload.search = search;
      filtersPayload.refNumber = refNumber;
      const response = await OrdersApi.getOrders(
        rootState.auth.account.token,
        filtersPayload
      );
      if (response.status === 200) {
        return {
          orders: response.data.content,
          pagination: {
            isLast: response.data.last,
            isFirst: response.data.first,
            specialFields: response.data.specialFields,
            total: response.data.totalElements,
          },
          concat: payload ? payload.concat ?? false : false,
        };
      }
      return { orders: [], pagination: { isLast: false, isFirst: false } };
    } catch (err) {
      showError(err, thunkAPI);
      return { orders: [], pagination: { isLast: false, isFirst: false } };
    } finally {
      thunkAPI.dispatch(setOrdersLoading({ type: 'orders', value: false }));
    }
  }
);

const fetchOrdersStatuses = createAsyncThunk(
  'orders/fetchStatuses',
  async (_, thunkAPI) => {
    const rootState = thunkAPI.getState() as RootState;
    const response = await OrdersApi.getOrderStatuses(
      rootState.auth.account.token
    );
    if (response.status === 200) {
      return response.data;
    }
    return [];
  }
);

const fetchOrdersRoles = createAsyncThunk(
  'orders/fetchRoles',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(setOrdersLoading({ type: 'roles', value: true }));
      const rootState = thunkAPI.getState() as RootState;
      const response = await OrdersApi.getRoles(rootState.auth.account.token);
      if (response.status === 200) {
        if (!rootState.orders.data.filters.role) {
          thunkAPI.dispatch(
            setSelectFilter({
              type: 'role',
              value:
                rootState.auth.profile.position === 'BUYER'
                  ? 'RECIPIENT'
                  : 'INITIATOR',
            })
          );
        }
        return response.data;
      }
      return [];
    } catch (err) {
      showError(err, thunkAPI);
      return [];
    } finally {
      thunkAPI.dispatch(setOrdersLoading({ type: 'roles', value: false }));
    }
  }
);

const fetchOrdersChanges = createAsyncThunk(
  'orders/changes',
  async ({ count }: { count?: number }, thunkAPI) => {
    const response = await OrdersApi.getOrdersChanges(count || 3);
    if (response.status === 200) {
      return response.data;
    }
    return [];
  }
);

export {
  fetchUserOrders,
  setOrdersFilter,
  setOrdersLoading,
  fetchOrdersRoles,
  setFiltersQuery,
  setSelectFilter,
  setPage,
  setInitialOrders,
  setPeriodData,
  fetchOrdersStatuses,
  fetchOrdersChanges,
};
